import { DiscountUpdateEvent } from '@theme/events';

/** Scheduled display only. Shopify's native BXGY discount owns all prices. */
(() => {
  const config = document.querySelector('[data-crown-hypergel-schedule]');
  if (!config) return;

  const startsAt = Date.parse(config.dataset.startsAt);
  const endsAt = Date.parse(config.dataset.endsAt);
  if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt) || endsAt <= startsAt) return;

  document.addEventListener('variant:update', (event) => {
    const picker = event.target;
    // Match the theme's main-product picker; quick-add must not change the PDP.
    if (picker.dataset?.templateProductMatch !== 'true' || picker.closest('product-card, quick-add-dialog')) return;
    const message = picker.closest('.shopify-section')?.querySelector('[data-crown-hypergel-product-message]');
    if (!message || message.dataset.productId !== event.detail.data.productId) return;
    message.hidden = !message.dataset.variantIds.split(',').includes(String(event.detail.resource?.id ?? ''));
  });

  const root = document.documentElement;
  const countdown = document.querySelector('[data-crown-hypergel-countdown]');
  let serverTime;
  let sampledAt;
  let active = false;
  let synchronizing = false;
  let refreshPending = false;
  let boundaryTimer;
  let countdownTimer;

  const now = () => serverTime + performance.now() - sampledAt;
  const renderCountdown = () => {
    clearTimeout(countdownTimer);
    if (!countdown) return;
    const remaining = active ? Math.max(0, Math.ceil((endsAt - now()) / 1000)) : 0;
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    countdown.textContent = [hours, minutes, seconds].map(value => String(value).padStart(2, '0')).join(':');
    if (active && remaining > 0 && !document.hidden) countdownTimer = setTimeout(renderCountdown, 1000);
  };
  const update = () => {
    clearTimeout(boundaryTimer);
    const time = now();
    const nextActive = time >= startsAt && time < endsAt;
    const changed = nextActive !== active;
    active = nextActive;
    renderCountdown();
    root.toggleAttribute('data-crown-hypergel-active', active);
    if (time < endsAt) {
      boundaryTimer = setTimeout(() => {
        update();
        // Recalculate the cart at the boundary, including an already open drawer.
        synchronize(true);
      }, Math.min((active ? endsAt : startsAt) - time, 2147483647));
    }
    return changed;
  };

  async function synchronize(refreshCart = false) {
    if (synchronizing) return;
    synchronizing = true;
    try {
      const url = new URL(config.dataset.cartUrl, location.origin);
      url.searchParams.set('_crown_clock', String(Date.now()));
      const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
      const date = Date.parse(response.headers.get('Date'));
      if (!response.ok || !Number.isFinite(date)) throw new Error('Shopify clock unavailable');
      let cart = await response.json();
      serverTime = date;
      sampledAt = performance.now();
      const changed = update();
      if (changed || refreshCart || refreshPending) {
        refreshPending = true;
        if (cart.item_count > 0 && now() >= startsAt) {
          // GET preserves old totals on carts created before a discount boundary.
          // An empty update asks Shopify to recalculate without replaying quantities
          // that could overwrite a simultaneous add/remove in another tab.
          const updated = await fetch(config.dataset.cartUpdateUrl, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: {} }),
          });
          if (!updated.ok) throw new Error('Shopify cart recalculation unavailable');
          cart = await updated.json();
        }
        refreshPending = false;
        document.dispatchEvent(new DiscountUpdateEvent(cart, 'crown-hypergel-promotion'));
      }
    } catch {
      // A failed request must never start an offer using an untrusted device clock.
      // An already synchronized page still closes on its monotonic boundary timer.
    } finally {
      synchronizing = false;
    }
  }

  const banner = document.querySelector('[data-crown-hypergel-banner]');
  if (banner) {
    new ResizeObserver(() => {
      root.style.setProperty('--crown-hypergel-banner-height', `${banner.offsetHeight}px`);
    }).observe(banner);
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) synchronize(true);
  });
  window.addEventListener('pageshow', () => synchronize(true));
  window.addEventListener('online', () => synchronize(true));
  setInterval(() => {
    if (!document.hidden && (!Number.isFinite(serverTime) || now() < endsAt)) synchronize();
  }, 60000);
  synchronize(true);
})();
